import { Alert, Button, Card, Result, Space, Typography, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmEmailVerification, getCurrentUser, sendEmailVerification } from '../services/api';
import type { CurrentUser, EmailVerificationConfirmResponse } from '../types/incident';

const { Paragraph, Text } = Typography;
type ConfirmStatus = 'idle' | 'confirming' | 'success' | 'failed';
const confirmationRequests = new Map<string, Promise<EmailVerificationConfirmResponse>>();

const confirmedTokenStorageKey = (token: string) => `gib-email-verification-confirmed:${token}`;

const readConfirmedEmail = (token: string): string | null => {
  try {
    return window.sessionStorage.getItem(confirmedTokenStorageKey(token));
  } catch {
    return null;
  }
};

const writeConfirmedEmail = (token: string, email: string) => {
  try {
    window.sessionStorage.setItem(confirmedTokenStorageKey(token), email);
  } catch {
    // Storage can be unavailable in restricted browser modes; the API remains idempotent.
  }
};

interface Props {
  currentUser: CurrentUser | null;
  onUserUpdated: (user: CurrentUser) => void;
}

const EmailVerificationPage = ({ currentUser, onUserUpdated }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(currentUser?.email_validation_requested_at || null);
  const [confirmStatus, setConfirmStatus] = useState<ConfirmStatus>('idle');
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const currentUserRef = useRef(currentUser);
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const storedEmail = readConfirmedEmail(token);
    if (storedEmail) {
      setConfirmedEmail(storedEmail);
      setConfirmStatus('success');
      return;
    }
    setConfirmStatus('confirming');
    let confirmationRequest = confirmationRequests.get(token);
    if (!confirmationRequest) {
      confirmationRequest = confirmEmailVerification(token);
      confirmationRequests.set(token, confirmationRequest);
    }
    confirmationRequest
      .then(async (response) => {
        writeConfirmedEmail(token, response.email);
        if (cancelled) return;
        setConfirmedEmail(response.email);
        setConfirmStatus('success');
        if (currentUserRef.current) {
          const user = await getCurrentUser();
          if (!cancelled) onUserUpdated(user);
        }
      })
      .catch(() => {
        confirmationRequests.delete(token);
        if (!cancelled) setConfirmStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [onUserUpdated, token]);

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await sendEmailVerification();
      setSentAt(response.email_validation_requested_at || new Date().toISOString());
      const user = await getCurrentUser();
      onUserUpdated(user);
      messageApi.success(t('emailVerification.sentMessage'));
    } catch {
      messageApi.error(t('emailVerification.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  if (token) {
    return (
      <div className="page">
        {messageContextHolder}
        <Card className="emailVerificationCard">
          {['idle', 'confirming'].includes(confirmStatus) && (
            <Result
              icon={<MailOutlined />}
              title={t('emailVerification.confirmingTitle')}
              subTitle={t('emailVerification.confirmingDescription')}
            />
          )}
          {confirmStatus === 'success' && (
            <Result
              status="success"
              title={t('emailVerification.confirmedTitle')}
              subTitle={confirmedEmail || currentUser?.email || undefined}
            />
          )}
          {confirmStatus === 'failed' && (
            <Space direction="vertical" size={16}>
              <Result
                status="warning"
                title={t('emailVerification.confirmFailedTitle')}
                subTitle={t('emailVerification.confirmFailedDescription')}
              />
              {currentUser && (
                <Button type="primary" loading={sending} onClick={handleSend}>
                  {t('emailVerification.resend')}
                </Button>
              )}
            </Space>
          )}
        </Card>
      </div>
    );
  }

  if (currentUser?.email_is_validated) {
    return (
      <div className="page">
        {messageContextHolder}
        <Result
          status="success"
          title={t('emailVerification.alreadyValidatedTitle')}
          subTitle={currentUser.email}
        />
      </div>
    );
  }

  return (
    <div className="page">
      {messageContextHolder}
      <Card className="emailVerificationCard">
        <Space direction="vertical" size={16}>
          <Result
            icon={<MailOutlined />}
            title={t(token ? 'emailVerification.linkTitle' : 'emailVerification.title')}
            subTitle={currentUser?.email || '-'}
          />
          {token && (
            <Alert
              type="info"
              showIcon
              message={t('emailVerification.linkPlaceholder')}
            />
          )}
          {sentAt && (
            <Alert
              type="success"
              showIcon
              message={t('emailVerification.sentTitle')}
              description={t('emailVerification.sentDescription')}
            />
          )}
          <Paragraph>{t('emailVerification.description')}</Paragraph>
          <ul className="emailVerificationNotes">
            <li>{t('emailVerification.notes.delay')}</li>
            <li>{t('emailVerification.notes.spam')}</li>
            <li>{t('emailVerification.notes.resend')}</li>
          </ul>
          <Space direction="vertical" size={8}>
            <Button type="primary" loading={sending} onClick={handleSend}>
              {t(sentAt ? 'emailVerification.resend' : 'emailVerification.send')}
            </Button>
            {sentAt && <Text type="secondary">{t('emailVerification.lastRequestedAt', { datetime: new Date(sentAt).toLocaleString() })}</Text>}
          </Space>
        </Space>
      </Card>
    </div>
  );
};

export default EmailVerificationPage;
